// services/collections.ts
import {
  apiClient,
  ApiError,
  ApiResponse,
  PaginatedApiResponse,
} from "@/lib/apiClient";
import { Category } from "@/types/models/category";
import { Product } from "@/types/models/product";
import { Wholesaler } from "@/types/models/wholesaler";

export const getWholesalersList = async ({
  page,
  pageSize,
}: {
  page: number;
  pageSize: number;
}): Promise<PaginatedApiResponse<Wholesaler[]>> => {
  try {
    const data: PaginatedApiResponse<Wholesaler[]> =
      await apiClient.getPaginated(`/api/v1/admin/:storeId/wholesaler`, {
        page: page.toString(),
        page_size: pageSize.toString(),
      });
    return {
      data: data.data,
      pagination: data.pagination,
      status: data.status,
      message: data.message,
      success: data.success,
      valid: data.valid,
    };
  } catch (error: unknown) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(500, "UNKNOWN_ERROR", "An unexpected error occurred");
  }
};

export const getWholesalers = async ({
  id,
}: {
  id: string;
}): Promise<ApiResponse<Wholesaler>> => {
  try {
    const data: ApiResponse<Wholesaler> = await apiClient.get(
      `/api/v1/admin/:storeId/wholesaler/${id}`,
    );
    return {
      data: data.data,
      status: data.status,
      message: data.message,
      success: data.success,
      valid: data.valid,
    };
  } catch (error: unknown) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(500, "UNKNOWN_ERROR", "An unexpected error occurred");
  }
};
export const getWholesalersProducts = async ({
  id,
  page,
  pageSize,
}: {
  id: string;
  page: number;
  pageSize: number;
}): Promise<PaginatedApiResponse<Product[]>> => {
  try {
    const data: PaginatedApiResponse<Product[]> = await apiClient.getPaginated(
      `/api/v1/admin/:storeId/wholesaler/${id}/products?page=${page}&page_size=${pageSize}`,
    );
    return {
      data: data.data,
      pagination: data.pagination,
      status: data.status,
      message: data.message,
      success: data.success,
      valid: data.valid,
    };
  } catch (error: unknown) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(500, "UNKNOWN_ERROR", "An unexpected error occurred");
  }
};
export const getWholesalersCollections = async ({
  id,
  page,
  pageSize,
}: {
  id: string;
  page: number;
  pageSize: number;
}): Promise<PaginatedApiResponse<Category[]>> => {
  try {
    const data: PaginatedApiResponse<Category[]> = await apiClient.getPaginated(
      `/api/v1/admin/:storeId/wholesaler/${id}/collections?page=${page}&page_size=${pageSize}&include_products=true`,
    );
    return {
      data: data.data,
      pagination: data.pagination,
      status: data.status,
      message: data.message,
      success: data.success,
      valid: data.valid,
    };
  } catch (error: unknown) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(500, "UNKNOWN_ERROR", "An unexpected error occurred");
  }
};

export const importWholesalersProducts = async ({
  id,
  margin,
  margin_type,
  payment_type,
  product_ids,
}: {
  id: string;
  margin: number;
  margin_type: string;
  payment_type: string[];
  product_ids: string[];
}): Promise<ApiResponse<Product[]>> => {
  try {
    const normalizedMargin =
      margin_type === "fixed" ? Math.round(margin * 100) : margin;
    const data: ApiResponse<Product[]> = await apiClient.put(
      `/api/v1/admin/:storeId/wholesaler/${id}/import-products`,
      {
        margin: normalizedMargin,
        margin_type,
        payment_type,
        product_ids,
      },
    );
    return {
      data: data.data,
      status: data.status,
      message: data.message,
      success: data.success,
      valid: data.valid,
    };
  } catch (error: unknown) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(500, "UNKNOWN_ERROR", "An unexpected error occurred");
  }
};

export const importWholesalersCollections = async ({
  id,
  margin,
  margin_type,
  payment_type,
  collection_ids,
}: {
  id: string;
  margin: number;
  margin_type: string;
  payment_type: string[];
  collection_ids: string[];
}): Promise<ApiResponse<Category[]>> => {
  try {
    const normalizedMargin =
      margin_type === "fixed" ? Math.round(margin * 100) : margin;
    const data: ApiResponse<Category[]> = await apiClient.put(
      `/api/v1/admin/:storeId/wholesaler/${id}/import-collections`,
      {
        margin: normalizedMargin,
        margin_type,
        payment_type,
        collection_ids,
      },
    );
    return {
      data: data.data,
      status: data.status,
      message: data.message,
      success: data.success,
      valid: data.valid,
    };
  } catch (error: unknown) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(500, "UNKNOWN_ERROR", "An unexpected error occurred");
  }
};

export const removeWholesalersProducts = async ({
  id,
  product_ids,
}: {
  id: string;
  product_ids: string[];
}): Promise<ApiResponse<Product[]>> => {
  try {
    const data: ApiResponse<Product[]> = await apiClient.put(
      `/api/v1/admin/:storeId/wholesaler/${id}/unmap-products`,
      {
        product_ids,
      },
    );
    return {
      data: data.data,
      status: data.status,
      message: data.message,
      success: data.success,
      valid: data.valid,
    };
  } catch (error: unknown) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(500, "UNKNOWN_ERROR", "An unexpected error occurred");
  }
};

export const removeWholesalersCollections = async ({
  id,
  collection_ids,
}: {
  id: string;
  collection_ids: string[];
}): Promise<ApiResponse<Category[]>> => {
  try {
    const data: ApiResponse<Category[]> = await apiClient.put(
      `/api/v1/admin/:storeId/wholesaler/${id}/unmap-collections`,
      {
        collection_ids,
      },
    );
    return {
      data: data.data,
      status: data.status,
      message: data.message,
      success: data.success,
      valid: data.valid,
    };
  } catch (error: unknown) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(500, "UNKNOWN_ERROR", "An unexpected error occurred");
  }
};
